import React from 'react';
import { ArrowRight, BadgeDollarSign, Building2, HeartHandshake, MapPin, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@mts/ui';

function CtaLink({ to, children, variant = 'primary' }) {
  return (
    <Button asChild variant={variant} size="xl" className="gap-2 rounded-md text-sm">
      <Link to={to}>
        {children}
        <ArrowRight className="h-4 w-4" />
      </Link>
    </Button>
  );
}

export function AboutHero({ eyebrow, title, subtitle, logoUrl, logoAlt }) {
  return (
    <section className="relative left-1/2 w-screen -translate-x-1/2 bg-surface">
      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-14 md:grid-cols-[minmax(0,1fr)_260px] md:items-center md:px-8 lg:py-16">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">{eyebrow}</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-bold leading-tight text-text-primary sm:text-5xl">
            {title}
          </h1>
          <p className="mt-5 max-w-3xl text-lg leading-8 text-text-secondary">{subtitle}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <CtaLink to="/offers">Explore Local Deals</CtaLink>
            <CtaLink to="/advertise/spotlight" variant="outline">Promote Your Business</CtaLink>
          </div>
        </div>
        <div className="hidden md:flex md:justify-end">
          <div className="flex aspect-square w-52 items-center justify-center rounded-lg border border-border bg-background p-8">
            {logoUrl ? (
              <img src={logoUrl} alt={logoAlt} className="max-h-full max-w-full object-contain" />
            ) : (
              <MapPin className="h-20 w-20 text-primary" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function AboutSection({ title, children, subtle = false }) {
  return (
    <section className={`relative left-1/2 w-screen -translate-x-1/2 ${subtle ? 'bg-background' : 'bg-surface'}`}>
      <div className="mx-auto max-w-4xl px-5 py-10 md:px-8">
        <h2 className="text-2xl font-bold text-text-primary">{title}</h2>
        <div className="mt-4 space-y-4 text-base leading-8 text-text-secondary">{children}</div>
      </div>
    </section>
  );
}

export function AboutHowItWorksCards({ cityName }) {
  const cards = [
    {
      title: 'For Residents',
      body: 'Discover local businesses, special offers, and helpful services in one easy place.',
      icon: Search,
    },
    {
      title: 'For Businesses',
      body: 'Get featured through affordable local marketing campaigns designed to reach nearby customers.',
      icon: BadgeDollarSign,
    },
    {
      title: 'For the Community',
      body: `More local discovery means more support for businesses that serve ${cityName}.`,
      icon: HeartHandshake,
    },
  ];

  return (
    <section className="relative left-1/2 w-screen -translate-x-1/2 bg-surface">
      <div className="mx-auto max-w-6xl px-5 py-12 md:px-8">
        <h2 className="text-2xl font-bold text-text-primary">How It Works</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <article key={card.title} className="rounded-lg border border-border bg-background p-5 shadow-sm">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-text-primary">{card.title}</h3>
                <p className="mt-2 text-sm leading-6 text-text-secondary">{card.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function BusinessOwnerCta({ siteName }) {
  return (
    <section className="relative left-1/2 w-screen -translate-x-1/2 bg-secondary text-secondary-content">
      <div className="mx-auto flex max-w-6xl flex-col gap-5 px-5 py-10 md:flex-row md:items-center md:justify-between md:px-8">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 text-sm font-semibold text-spotlight">
            <Building2 className="h-4 w-4" />
            Local Business Owners
          </div>
          <p className="mt-3 text-xl font-semibold leading-8">
            Own a local business? {siteName} gives you a cost-effective way to reach nearby households, increase awareness, and attract more local customers.
          </p>
        </div>
        <Button asChild variant="primary" size="xl" className="gap-2 rounded-md bg-spotlight text-spotlight-content hover:bg-spotlight/90">
          <Link to="/advertise/spotlight">
            Advertise With Us
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </section>
  );
}

export function TenantRichTextRenderer({ content }) {
  const normalizedContent = String(content || '').trim();
  const hasHtmlContent = /<\/?[a-z][\s\S]*>/i.test(normalizedContent);

  if (hasHtmlContent) {
    return (
      <div
        className="prose prose-slate max-w-none prose-a:font-semibold prose-a:text-primary hover:prose-a:text-primary-hover prose-blockquote:border-l-primary prose-blockquote:text-text-secondary"
        dangerouslySetInnerHTML={{ __html: normalizedContent }}
      />
    );
  }

  const blocks = String(content || '')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  if (!blocks.length) return null;

  return (
    <div className="space-y-4">
      {blocks.map((block, index) => (
        <p key={`${index}-${block.slice(0, 12)}`} className="whitespace-pre-line">
          {block}
        </p>
      ))}
    </div>
  );
}
